import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { HrailRailJunction } from "../../../../../http/hrail/types/railJunctions";
import { useHrailRailJunctionsApi } from "../../../../../http/hrail/useRailJunctions";
import { AdminItem } from "../../../../../http/types/items";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateRailJunctionModalDialogButton from "./CreateRailJunctionModalDialogButton";
import RailJunctionForm from "./RailJunctionForm";

type Props = {
  active?: boolean;
};

const RailJunctions: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "railJunctions" });
  const aborter = useRef(new AbortController());
  const railJunctionApi = useHrailRailJunctionsApi();
  const [itemToEdit, setItemToEdit] = useState<HrailRailJunction>();
  const [itemToDelete, setItemToDelete] = useState<HrailRailJunction>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "railJunctions");
  const isActiveTab = active ?? true;
  const updateVisibility = async (railJunction: HrailRailJunction, visibility: VisibilityScopeValue) => {
    try {
      await railJunctionApi.updateAdminHrailRailJunctionVisibility(railJunction.item.id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (railJunction: HrailRailJunction, newOrder: number) => {
    try {
      await railJunctionApi.updateAdminHrailRailJunctionOrder(railJunction.item.id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const { accessesAction, accessesDialog } = useEntityAccesses<AdminItem>("hrail.rail_junction");

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = railJunctionApi.useAdminHrailRailJunctions(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.railJunctions.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.railJunctions.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateRailJunctionModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<HrailRailJunction>
        isTall={true}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.items.singular}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(railJunction) => (
          <>
            <Table.Td noWrap={true}>{railJunction.item?.code ?? ""}</Table.Td>
            <Table.Td noWrap={false}>{railJunction.item?.description ?? ""}</Table.Td>
            <td align="center">
              <Visibility value={railJunction.visibility} onChange={(value) => updateVisibility(railJunction, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor
                order={railJunction.order}
                onUpdate={(newOrder) => updateOrder(railJunction, newOrder)}
              />
            </td>
          </>
        )}
        onRowClick={(railJunction) => setItemToEdit(railJunction)}
        rowActions={(railJunction) => [
          getEditAction(i18n, () => setItemToEdit(railJunction)),
          accessesAction(railJunction.item),
          getDeleteAction(i18n, () => setItemToDelete(railJunction), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.railJunctions.edit}>
          <RailJunctionForm railJunction={itemToEdit} onClose={() => setItemToEdit(undefined)} />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.railJunctions.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                railJunctionApi
                  .deleteAdminHrailRailJunction(itemToDelete.item.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.common.deleteEntityQuestion(
            i18n.translation.railJunctions.deleteLabel,
            [itemToDelete.item?.code ?? "", itemToDelete.item?.description ?? ""].filter(Boolean).join(" - ") ||
              itemToDelete.item.id,
          )}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default RailJunctions;
